import React, { useState, useEffect, useMemo } from 'react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  SortingState,
  getSortedRowModel,
  VisibilityState,
  getPaginationRowModel,
  OnChangeFn,
  ColumnFiltersState,
  getFilteredRowModel,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { debounce } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { DateRange } from 'react-day-picker';
import { format } from 'date-fns';
import { VoucherResponse } from '@/app/types/voucher';

// Define a type for the custom meta properties
interface CustomColumnMeta {
  label?: string;
  placeholder?: string;
  variant?: 'text' | 'number' | 'select' | 'date' | 'dateRange' | 'boolean' | 'multiSelect';
  icon?: React.ComponentType<{ className?: string }>;
  options?: Array<{ label: string; value: string | number | boolean }>;
}

// Use intersection type for ExtendedColumnDef
type ExtendedColumnDef<TData, TValue = unknown> = ColumnDef<TData, TValue> & {
  meta?: CustomColumnMeta;
};

interface DataTableProps<TData, TValue> {
  columns: ExtendedColumnDef<TData, TValue>[];
  data: TData[];
  isLoading?: boolean;
  pageCount: number;
  page: number;
  setPage: (page: number) => void;
  limit: number;
  totalRecords: number;
  onSortingChange?: (sorting: SortingState) => void;
  sortingState?: SortingState;
  onFilterChange?: (filters: Record<string, string | number | boolean | { from?: string; to?: string }>) => void;
  initialFilters?: Record<string, string | number | boolean | string[] | { from?: string; to?: string }>;
}

export function ExpenseDataTable<TData extends VoucherResponse, TValue>({
  columns,
  data,
  isLoading = false,
  pageCount,
  page,
  setPage,
  limit,
  totalRecords,
  onSortingChange,
  sortingState = [],
  onFilterChange,
  initialFilters,
}: DataTableProps<TData, TValue>): JSX.Element {
  const [sorting, setSorting] = useState<SortingState>(sortingState);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  
  // Initialize column filters from initialFilters prop
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(() => {
    if (!initialFilters) return [];
    
    const filters: ColumnFiltersState = [];
    Object.entries(initialFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        filters.push({ id: key, value });
      }
    });
    return filters;
  });
  
  const navigate = useNavigate();
  
  // Sync initial filters on first mount only
  const [hasInitialized, setHasInitialized] = useState(false);
  useEffect(() => {
    if (!hasInitialized && initialFilters) {
      const filters: ColumnFiltersState = [];
      Object.entries(initialFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          filters.push({ id: key, value });
        }
      });
      setColumnFilters(filters);
      setHasInitialized(true);
    }
  }, [hasInitialized, initialFilters]);

  // Create a stable debounced filter change handler
  const debouncedFilterChange = React.useMemo(
    () => debounce((filters: Record<string, string | number | boolean | string[] | { from?: string; to?: string }>) => {
      console.log('Debounced filter change - calling onFilterChange with:', filters);
      onFilterChange?.(filters);
    }, 300),
    [onFilterChange]
  );

  // Use useEffect to send filter changes to parent component
  useEffect(() => {
    // Skip the initial render to prevent clearing URL filters
    if (!hasInitialized) {
      return;
    }
    if (onFilterChange) {
      // Convert column filters to a format that can be used by the API
      const apiFilters: Record<string, string | number | boolean | { from?: string; to?: string }> = {};
      
      columnFilters.forEach((filter) => {
        const value = filter.value;
        if (value !== undefined && value !== null && value !== '') {
          // Handle date range objects
          if (typeof value === 'object' && 'from' in value) {
            const dateRange = value as { from?: string; to?: string };
            // Only add if at least one date is set
            if (dateRange.from || dateRange.to) {
              apiFilters[filter.id] = dateRange;
            }
          }
          // Handle simple values
          else {
            apiFilters[filter.id] = value;
          }
        }
      });
      
      console.log('Column filters changed:', columnFilters);
      console.log('API filters to send:', apiFilters);
      debouncedFilterChange(apiFilters);
    }
  }, [columnFilters, onFilterChange, debouncedFilterChange, hasInitialized]);

  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    const newSorting = updater instanceof Function ? updater(sorting) : updater;
    setSorting(newSorting);
    if (onSortingChange) {
      onSortingChange(newSorting);
    }
    setPage(0); // Reset to page 0 when sorting changes
  };

  const handleColumnFiltersChange = (updater: ColumnFiltersState | ((state: ColumnFiltersState) => ColumnFiltersState)): void => {
    const newFilters = updater instanceof Function ? updater(columnFilters) : updater;
    setColumnFilters(newFilters);
    // Reset to first page when filters change
    setPage(0);
  };

  const table = useReactTable({
    data,
    columns: columns as ColumnDef<TData, TValue>[],
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: handleSortingChange,
    getSortedRowModel: getSortedRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onColumnFiltersChange: handleColumnFiltersChange,
    getFilteredRowModel: getFilteredRowModel(),
    manualPagination: true, // Important for server-side pagination
    manualSorting: true, // Important for server-side sorting
    manualFiltering: true, // Important for server-side filtering
    pageCount,
    state: {
      sorting,
      columnVisibility,
      columnFilters,
      pagination: {
        pageIndex: page,
        pageSize: limit,
      },
    },
  });

  // Pagination info
  const startRecord = page * limit + 1;
  const endRecord = Math.min((page + 1) * limit, totalRecords);

  // Helper to get column key
  const getColumnKey = (column: ExtendedColumnDef<TData, TValue>): string => {
    return column.id ?? (column as { accessorKey?: string }).accessorKey ?? '';
  };

  const renderPaginationControls = (): JSX.Element => (
    <div className="flex items-center justify-between w-full mt-4">
      <div className="text-sm text-muted-foreground">
        Showing {startRecord} to {endRecord} of {totalRecords} records
      </div>
      <div className="flex items-center space-x-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPage(page - 1)}
          disabled={page === 0 || isLoading}
        >
          <ChevronLeft className="h-4 w-4" />
          Previous
        </Button>
        <span className="text-sm">
          Page {page + 1} of {pageCount || 1}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPage(page + 1)}
          disabled={page + 1 >= pageCount || isLoading}
        >
          Next
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="w-full h-[400px] flex flex-col items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
        <div className="text-muted-foreground">Loading expenses...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-4">
        {/* Filter inputs row */}
        <div className="flex items-center gap-2 flex-wrap">
          {columns
            .filter((column) => column.enableColumnFilter && column.meta)
            .map((column) => {
              const meta = column.meta as CustomColumnMeta;
              const columnKey = getColumnKey(column);
              const columnFilterValue = table.getColumn(columnKey)?.getFilterValue();
              
              if (meta.variant === 'text' || meta.variant === 'number') {
                return (
                  <Input
                    key={columnKey}
                    placeholder={meta.placeholder || `Filter ${meta.label}...`}
                    value={(columnFilterValue ?? '') as string}
                    onChange={(e) => table.getColumn(columnKey)?.setFilterValue(e.target.value)}
                    className="h-8 w-[150px] text-sm"
                    type={meta.variant === 'number' ? 'number' : 'text'}
                  />
                );
              }
              
              if (meta.variant === 'select' && meta.options) {
                return (
                  <Select
                    key={columnKey}
                    value={(columnFilterValue as string) || 'all'}
                    onValueChange={(value) => {
                      table.getColumn(columnKey)?.setFilterValue(value === 'all' ? undefined : value);
                    }}
                  >
                    <SelectTrigger className="h-8 w-[150px] text-sm">
                      <SelectValue placeholder={meta.placeholder || `All ${meta.label}`} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      {meta.options.map((option) => (
                        <SelectItem key={String(option.value)} value={String(option.value)}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                );
              }
              
              if (meta.variant === 'dateRange') {
                const dateRangeValue = columnFilterValue as { from?: string; to?: string } | undefined;
                const dateRange: DateRange | undefined = dateRangeValue ? {
                  from: dateRangeValue.from ? new Date(dateRangeValue.from) : undefined,
                  to: dateRangeValue.to ? new Date(dateRangeValue.to) : undefined,
                } : undefined;
                
                return (
                  <DateRangePicker
                    key={columnKey}
                    date={dateRange}
                    onDateChange={(newDateRange) => {
                      if (!newDateRange || (!newDateRange.from && !newDateRange.to)) {
                        table.getColumn(columnKey)?.setFilterValue(undefined);
                      } else {
                        table.getColumn(columnKey)?.setFilterValue({
                          from: newDateRange.from ? format(newDateRange.from, 'yyyy-MM-dd') : undefined,
                          to: newDateRange.to ? format(newDateRange.to, 'yyyy-MM-dd') : undefined,
                        });
                      }
                    }}
                  />
                );
              }
              
              return null;
            })}
          
          {/* Clear filters button */}
          {columnFilters.length > 0 && (
            <Button
              variant="ghost"
              onClick={() => table.resetColumnFilters()}
              className="h-8 px-2 lg:px-3"
            >
              Reset
            </Button>
          )}
        </div>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No expenses found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination controls */}
      {renderPaginationControls()}
    </div>
  );
} 